import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Heart, Loader2, BarChart3, TrendingUp } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { AnalyticsTimeSeriesData } from '@/types/social';
import { cn } from '@/lib/core/utils';

interface EngagementChartProps {
  data: AnalyticsTimeSeriesData[];
  isLoading: boolean;
}

type ChartType = 'area' | 'bar';
type MetricType = 'engagement' | 'reach' | 'impressions';

export const EngagementChart: React.FC<EngagementChartProps> = ({
  data,
  isLoading
}) => {
  const [chartType, setChartType] = useState<ChartType>('area');
  const [metric, setMetric] = useState<MetricType>('engagement');

  // Format data for chart
  const chartData = data.map(item => ({
    ...item,
    dateLabel: format(parseISO(item.date), 'MMM d'),
  }));

  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toString();
  };

  const getMetricColor = (m: MetricType) => {
    switch (m) {
      case 'engagement': return 'hsl(var(--chart-1))';
      case 'reach': return 'hsl(var(--chart-2))';
      case 'impressions': return 'hsl(var(--chart-3))';
    }
  };

  const getMetricLabel = (m: MetricType) => {
    switch (m) {
      case 'engagement': return 'Engagement';
      case 'reach': return 'Reach';
      case 'impressions': return 'Impressions';
    }
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload) return null;

    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-2">{label}</p>
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2 text-sm">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-muted-foreground">{entry.name}:</span>
            <span className="font-medium">{formatYAxis(entry.value)}</span>
          </div>
        ))}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-lg">Engagement & Reach</CardTitle>
              <CardDescription>Performance metrics over time</CardDescription>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Chart type toggle */}
            <div className="flex items-center border rounded-lg p-0.5">
              <Button
                variant={chartType === 'area' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setChartType('area')}
              >
                <TrendingUp className="h-3 w-3" />
              </Button>
              <Button
                variant={chartType === 'bar' ? 'secondary' : 'ghost'}
                size="sm"
                className="h-7 px-2"
                onClick={() => setChartType('bar')}
              >
                <BarChart3 className="h-3 w-3" />
              </Button>
            </div>
          </div>
        </div>

        {/* Metric selector */}
        <div className="flex gap-2 mt-2">
          {(['engagement', 'reach', 'impressions'] as MetricType[]).map((m) => (
            <Button
              key={m}
              variant={metric === m ? 'default' : 'outline'}
              size="sm"
              className="h-7"
              onClick={() => setMetric(m)}
            >
              {getMetricLabel(m)}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[300px] flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : data.length === 0 ? (
          <div className="h-[300px] flex items-center justify-center">
            <p className="text-muted-foreground">No data available for this period</p>
          </div>
        ) : (
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              {chartType === 'area' ? (
                <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={formatYAxis}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey={metric}
                    name={getMetricLabel(metric)}
                    stroke={getMetricColor(metric)}
                    fill={getMetricColor(metric)}
                    fillOpacity={0.2}
                    strokeWidth={2}
                  />
                </AreaChart>
              ) : (
                <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="dateLabel"
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <YAxis
                    tickFormatter={formatYAxis}
                    tick={{ fontSize: 12 }}
                    tickLine={false}
                    axisLine={false}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey={metric}
                    name={getMetricLabel(metric)}
                    fill={getMetricColor(metric)}
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EngagementChart;
